import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import MobileBottomNav from '../components/MobileBottomNav';
import '../styles/AuthPages.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const token = searchParams.get('token');

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenValid(false);
        setVerifying(false);
        return;
      }
      
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/verify-reset-token/${token}`);
        setTokenValid(response.data.valid);
      } catch (err) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate passwords
    if (password.length < 6) {
      setError(t('passwordMinLength') || 'Password must be at least 6 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError(t('passwordsNoMatch') || 'Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        password
      });
      
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (err) {
      setError(err.response?.data?.detail || t('resetPasswordError') || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state while verifying token
  if (verifying) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <div className="auth-icon">
              <Lock size={32} />
            </div>
            <h1>{t('resetPasswordTitle') || 'Reset Password'}</h1>
            <p>{t('verifyingToken') || 'Verifying your reset link...'}</p>
          </div>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Invalid or expired token
  if (!tokenValid) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <div className="auth-icon error">
              <AlertCircle size={32} />
            </div>
            <h1>{t('invalidToken') || 'Invalid or Expired Link'}</h1>
            <p>{t('invalidTokenDesc') || 'This password reset link is invalid or has expired. Please request a new one.'}</p>
          </div>
          <Link to="/forgot-password" className="btn-primary" style={{ width: '100%', textAlign: 'center' }}>
            {t('requestNewLink') || 'Request New Link'}
          </Link>
          <Link to="/login" className="back-link">
            <ArrowLeft size={18} />
            {t('backToLogin') || 'Back to Login'}
          </Link>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <div className="auth-icon success">
              <CheckCircle size={32} />
            </div>
            <h1>{t('passwordResetSuccess') || 'Password Reset Successfully!'}</h1>
            <p>{t('passwordResetSuccessDesc') || 'Your password has been updated. Redirecting to login...'}</p>
          </div>
          <Link to="/login" className="btn-primary" style={{ width: '100%', textAlign: 'center' }}>
            {t('goToLogin') || 'Go to Login'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <Helmet>
        <title>Reset Password - Registan Tourism</title>
        <link rel="canonical" href="https://registantours.com/reset-password" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-icon">
            <Lock size={32} />
          </div>
          <h1>{t('resetPasswordTitle') || 'Reset Password'}</h1>
          <p>{t('resetPasswordDesc') || 'Enter your new password below.'}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label>{t('newPassword') || 'New Password'}</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('enterNewPassword') || 'Enter new password'}
                required
                minLength={6}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>{t('confirmPassword') || 'Confirm Password'}</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('confirmNewPassword') || 'Confirm new password'}
                required
                minLength={6}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? (t('resetting') || 'Resetting...') : (t('resetPassword') || 'Reset Password')}
          </button>
        </form>

        <Link to="/login" className="back-link">
          <ArrowLeft size={18} />
          {t('backToLogin') || 'Back to Login'}
        </Link>
        <p style={{ marginTop: '14px', textAlign: 'center', fontSize: '12px', opacity: 0.7 }}>
          <Link to="/privacy" style={{ color: 'inherit' }} data-testid="auth-privacy-link">{t('privacyPolicy') || 'Privacy Policy'}</Link>
        </p>
      </div>
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default ResetPasswordPage;
